/**
 * Dialog for loading a pre-existing case into the SIM keyword data fields.
 *
 * @module components/sim/keywords/LoadCase
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
/* eslint-disable radix */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog, DialogTitle, DialogContent,
    DialogContentText, Divider, DialogActions,
    Button, TextField,
} from '@material-ui/core';
import { Autocomplete, Alert } from '@material-ui/lab';
import { connect } from 'react-redux';
import {
    fetchAllCaseId,
    fetchCaseDetails,
    sfReset,
    setDataLoaded,
} from '../../../redux/sf/actions';

/**
 * LoadCase component is responsible for loading and displaying details of a selected case.
 * It allows users to select a case ID and fetches corresponding details from the backend.
 * The component handles success and failure states for fetching case details and displays
 * appropriate notifications.
 *
 * @param {Object} props - The properties passed to the LoadCase component.
 * @param {boolean} props.showModal - Controls the visibility of the dialog modal.
 * @param {function} props.setShowModal - Function to set the visibility state of the dialog modal.
 * @param {Array} props.caseIds - List of available case identifiers to be displayed in the dropdown.
 * @param {function} props.fetchAllCaseId - Function to retrieve all case identifiers.
 * @param {function} props.fetchCaseDetails - Function to fetch details of a specific case ID.
 * @param {Object} props.caseDetails - Data object containing the details of a case.
 * @param {number} props.status - HTTP status code for the operation's success or failure state.
 * @param {function} props.sfReset - Function to reset state or data when loading new case details.
 * @param {function} props.setDataLoaded - Function to update the state when case details have been successfully loaded.
 */
const LoadCase = ({
    showModal,
    setShowModal,
    caseIds,
    fetchAllCaseId,
    fetchCaseDetails,
    caseDetails,
    status,
    sfReset,
    setDataLoaded,
}) => {
    const [open, setOpen] = useState(showModal);
    const [caseId, setCaseId] = useState(null);
    const [loadSuccess, setLoadSuccess] = useState(null);

    useEffect(() => {
        fetchAllCaseId();
    }, [fetchAllCaseId]);

    useEffect(() => {
        setLoadSuccess(null);
    }, [caseIds]);

    useEffect(() => {
        if (status === 200 && Object.keys(caseDetails).length > 3) {
            setLoadSuccess(true);
            setDataLoaded(true);
        } else if (status === 500) {
            setLoadSuccess(false);
            setDataLoaded(false);
        }
    }, [status, caseDetails, setDataLoaded]);

    /**
     * A function that handles closing actions by updating the state variables.
     * Specifically, it sets `open` and `showModal` to `false`, effectively
     * closing or hiding relevant UI components.
     */
    const handleClose = () => {
        setOpen(false);
        setShowModal(false);
    };

    /**
     * Handles the change event for an input field and updates the case ID state.
     *
     * @param {Object} event - The event object containing details about the input change.
     * @param {Object} event.target - The target element where the event occurred.
     * @param {string} event.target.value - The new value from the input field.
     */
    const handleChange = (event) => {
        setCaseId(event.target.value);
    };

    /**
     * Handles the load event by performing specific actions
     * when a case ID is present.
     *
     * This function checks if a `caseId` is defined and, if so,
     * resets the state using `sfReset` and retrieves case details
     * by calling `fetchCaseDetails` with the parsed integer value
     * of the `caseId`.
     *
     * @function
     */
    const handleLoad = () => {
        if (caseId) {
            sfReset();
            fetchCaseDetails(parseInt(caseId));
        }
    };

    if (loadSuccess) {
        return (
            <Alert
                onClose={() => {
                    setShowModal(false);
                    sfReset();
                }}
                variant="filled"
                severity="success"
            >
                Case Details loaded successfully — check it out!
            </Alert>
        );
    }

    if (loadSuccess === false) {
        return (
            <Alert onClose={() => setShowModal(false)} variant="filled" severity="error" style={{ backgroundColor: '#600727' }}>
                Case Details not loaded — Please try another case_number!
            </Alert>
        );
    }

    return (
        <div>
            <Dialog open={open} fullWidth onClose={handleClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">Load Case Details</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Select case number to load its full details
                    </DialogContentText>
                    <Autocomplete
                        id="free-solo-demo"
                        freeSolo
                        options={caseIds.map((option) => option.caseNumber.toString())}
                        value={caseId}
                        onChange={(event, newValue) => {
                            setCaseId(newValue);
                        }}
                        renderInput={(params) => (
                            <TextField {...params} type="number" label="case_number" margin="normal" variant="outlined" value={caseId} onChange={handleChange} />
                        )}
                    />
                    { }
                    <Divider variant="fullWidth" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} style={{ color: '#313387' }}>
                        Cancel
                    </Button>
                    <Button onClick={() => handleLoad()} color="primary">
                        Load
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

/**
 * Defines the prop types for the LoadCase component.
 *
 * This specifies the expected types and requirements for the props that
 * the LoadCase component should receive. Prop types help ensure that the
 * correct data types are passed to the component, and they enable runtime
 * warnings during development if invalid types are provided.
 */
LoadCase.propTypes = {
    showModal: PropTypes.bool,
    setShowModal: PropTypes.func,
    caseIds: PropTypes.array,
    fetchAllCaseId: PropTypes.func,
    caseDetails: PropTypes.object,
    status: PropTypes.number,
    fetchCaseDetails: PropTypes.func,
    sfReset: PropTypes.func,
    setDataLoaded: PropTypes.func,
};

/**
 * Maps the Redux state to the component's props.
 *
 * @function mapStateToProps
 * @param {Object} state - The current Redux state.
 * @returns {Object} An object containing specific properties from the Redux state:
 * - `caseIds`: An array or collection of case identifiers from the state.
 * - `caseDetails`: Details or metadata associated with the cases from the state.
 * - `status`: The current status or state of the cases.
 */
const mapStateToProps = (state) => ({
    caseIds: state.sf.caseIds,
    caseDetails: state.sf.caseDetails,
    status: state.sf.status,
});

/**
 * Object defining the mapping of Redux action creators to dispatchable functions.
 * This object provides a set of actions that can be dispatched to modify the state of the application.
 *
 * @property {Function} fetchAllCaseId - Dispatches an action to fetch all case IDs.
 * @property {Function} fetchCaseDetails - Dispatches an action to fetch details for a specific case.
 * @property {Function} sfReset - Dispatches an action to reset specific stateful properties, typically for cleanup.
 * @property {Function} setDataLoaded - Dispatches an action to update the state indicating data loading completion.
 */
const mapDispatchToProps = {
    fetchAllCaseId,
    fetchCaseDetails,
    sfReset,
    setDataLoaded,
};

// connect salesforce redux state

export default connect(mapStateToProps, mapDispatchToProps)(LoadCase);
